/* Black Sand Sauna — shared "Add to cart" for our custom buttons
 * (booking widget + pass cards). Exposes window.SaunaCart.addLines().
 *
 * ⚠ READ BEFORE EDITING — this file exists because of a bug that came back
 * three times ("cart bubble/drawer don't update after Add to cart until you
 * refresh"). The hard-won rules:
 *
 * 1. THIS MUST STAY AN ES MODULE WITH A *STATIC* `import … from '@shopify/events'`,
 *    loaded with <script type="module">. Shopify's CDN minifies theme JS assets
 *    when it serves them. It keeps static ESM imports (every Horizon file uses
 *    them), but it rewrote a dynamic `import('@shopify/events')` inside our
 *    classic script into `require("@shopify/events")`. That throws in every
 *    browser, and the old code swallowed it. The source in the Admin API looked
 *    perfect, and only the *served* file was broken.
 *    → Never use dynamic import() for bare specifiers in a theme asset.
 *    → Verify the SERVED file after deploy: booking_system/tests/smoke → `npm run cart`.
 *
 * 2. We copy exactly what Horizon's own product-form.js does: POST /cart/add.js
 *    with `sections` = every <cart-items-component>'s section id, dispatch
 *    CartLinesUpdateEvent, and resolve it with the cart AND the returned
 *    `sections` HTML. The theme's cart icon, drawer and cart page then redraw
 *    themselves. Sending the event without `sections` pushes the drawer onto a
 *    weaker re-fetch path.
 *
 * 3. We don't trust it blindly. After every add, verifyThemeCart() checks the
 *    real DOM (bubble count + drawer not "empty"). If the theme didn't refresh
 *    (a future Horizon change, a theme swap), it repairs the UI itself through
 *    the Section Rendering API and logs a console.error. The result is recorded
 *    on <html data-sauna-cart="ok|repaired|failed"> so the smoke test
 *    (tests/smoke) can tell "native path works" apart from "saved by the repair".
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const EMPTY_DRAWER = '.cart-drawer--empty, [data-cart-drawer-empty]';

/* Terms & Conditions tick: ONE record per visit, the cart attribute
 * "Terms accepted" ("Yes" or removed). Every tick box on the site (the booking
 * drawer's .sauna-terms-box, the cart page/drawer's .bss-cart-terms__box) reads
 * it and writes it through setTermsAccepted(), so a customer who ticks once
 * never has to tick again. The cart box is rendered pre-ticked from the
 * attribute by Liquid (snippets/cart-summary.liquid). It carries
 * data-skip-node-update, so Horizon's morph never touches an existing box;
 * syncTermsBoxes() sets those directly. */
const TERMS_ATTRIBUTE = 'Terms accepted';
const TERMS_BOXES = '.bss-cart-terms__box, .sauna-terms-box';

function syncTermsBoxes(accepted) {
  for (const box of document.querySelectorAll(TERMS_BOXES)) {
    box.checked = accepted;
    box.setCustomValidity?.('');
    box.removeAttribute('aria-invalid');
  }
}

async function setTermsAccepted(accepted) {
  accepted = Boolean(accepted);
  syncTermsBoxes(accepted);
  document.dispatchEvent(new CustomEvent('sauna-cart:terms', { detail: { accepted } }));
  // A blank value removes the attribute, so an untick is recorded too.
  await readJSON(
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [TERMS_ATTRIBUTE]: accepted ? 'Yes' : '' } }),
    })
  );
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function readJSON(response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((data && (data.description || data.message)) || `HTTP ${response.status}`);
  }
  return data;
}

function setStatus(status) {
  document.documentElement.dataset.saunaCart = status;
}

/* Add `items` ([{id, quantity, properties?}]) to the cart and refresh the
 * theme's cart UI. Resolves with {added, cart}; rejects if the add failed.
 * `termsAccepted` (optional boolean): the booking drawer's tick state at the
 * moment of the add. It's saved BEFORE the add, so the cart HTML Shopify
 * returns with the add already shows the right tick. */
async function addLines(items, { source = 'bss', termsAccepted } = {}) {
  if (typeof termsAccepted === 'boolean') {
    await setTermsAccepted(termsAccepted).catch((err) =>
      console.error('[sauna-cart] could not save the terms tick (the cart box will ask again):', err)
    );
  }
  const sectionIds = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  setStatus('pending');

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({ merchandiseId: it.id, quantity: it.quantity })),
      promise: deferred.promise,
    })
  );

  let added;
  let cart;
  try {
    added = await readJSON(
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      })
    );
    cart = await readJSON(await fetch('/cart.js', { headers: { Accept: 'application/json' } }));
  } catch (err) {
    deferred.resolve({ cart: null, detail: { source, didError: true } });
    setStatus('failed');
    throw err;
  }

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source,
      itemCount: items.reduce((n, it) => n + (Number(it.quantity) || 1), 0),
      sections: added && added.sections,
      didError: false,
    },
  });

  verifyThemeCart(cart, sectionIds);
  return { added, cart };
}

/* Is the theme's cart UI showing what the server says is in the cart? */
function themeCartIsStale(cart) {
  const count = cart.item_count;
  for (const icon of document.querySelectorAll('cart-icon')) {
    const shown = Number(icon.currentCartCount);
    if (!Number.isNaN(shown) && shown !== count) return 'bubble shows ' + shown + ', cart has ' + count;
  }
  if (count > 0) {
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el.querySelector(EMPTY_DRAWER) || el.closest(EMPTY_DRAWER)) return 'drawer still shows an empty cart';
    }
  }
  return '';
}

/* Give the theme time to morph (view transitions run ~0.5s), then check. If it
 * didn't refresh, repair: re-render the cart sections and set the bubble. */
function verifyThemeCart(cart, sectionIds) {
  setTimeout(async () => {
    const problem = themeCartIsStale(cart);
    if (!problem) return setStatus('ok');

    console.error('[sauna-cart] theme cart UI did not refresh (' + problem + ') — repairing. ' +
      'The native CartLinesUpdateEvent path is broken; see theme/assets/sauna-cart.js.');
    try {
      if (sectionIds.length) {
        const url = window.location.pathname + '?sections=' + encodeURIComponent(sectionIds.join(','));
        const html = await readJSON(await fetch(url));
        for (const id of sectionIds) {
          const live = document.getElementById('shopify-section-' + id);
          if (!live || !html[id]) continue;
          const doc = new DOMParser().parseFromString(html[id], 'text/html');
          const fresh = doc.getElementById('shopify-section-' + id);
          if (fresh) live.replaceWith(fresh);
        }
      }
      for (const icon of document.querySelectorAll('cart-icon')) {
        if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false);
      }
      const still = themeCartIsStale(cart);
      if (still) throw new Error(still);
      setStatus('repaired');
    } catch (err) {
      console.error('[sauna-cart] repair failed:', err);
      setStatus('failed');
    }
  }, 1500);
}

window.SaunaCart = { addLines, setTermsAccepted, version: 3 };
document.dispatchEvent(new Event('sauna-cart:ready'));
